import { useCallback, useContext, useMemo } from 'react'
import semver from 'semver'
import { AppContext, AppContextType, UpgradeToMuseProDialogType } from '../AppContext'
import { WaitlistItem } from '../pages/CollaboratePage/Types'
import {
    ApiRoomDocument,
    AudioEngineRemoteAudio,
    loadInitialRoomContext,
    P2pRemoteAudio,
    PinKind,
    RoomContext,
    RoomContextState,
    RoomContextType,
    SfuRemoteAudio,
    toSessionState,
} from '../RoomContext'
import { AudioTrackWithInfo, SfuNode, UserContext, UserContextType } from '../UserContext'
import useRoomContextRef from './useRoomContextRef'
import { RemoteParticipant } from 'livekit-client'
import { wsConnection } from './WSConnectionInstance'
import { logger } from './Logger'
import { useHistory } from 'react-router-dom'
import { unstable_batchedUpdates } from 'react-dom'
import ElectronIPC from './ElectronIPC'
import { MusePeer, PeerManifest } from './RoomContextMusePeer'
import useRemoteRecordingActions from './useRemoteRecordingActions'
import useUserContextRef from './useUserContextRef'
import { v4 as uuidv4 } from 'uuid'
import { isSubset, numberToColumnLabel, sleep, stripSemverVersion } from '../utils'
import { WSConnectionSendError } from './WSConnectionManager'
import useRootSnackbar from './useRootSnackbar'
import { env } from './EnvironmentVariables'
import * as Constants from '../services/Constants'
import { sfuNodeSelection } from './SfuNodeSelection'
import { persistent } from './LocalStorage'
import { VolumePerChannelGroup } from '../core/AudioEngine/FakeMediaStreamTrack'
import { createCustomJoinLink, encodeExtendedDeviceId } from '../Logic'
import useAppContextRef from './useAppContextRef'
import { AnalyticsEvents } from './AnalyticsService'
import useAnalytics from './useAnalytics'
import { useMutate } from 'restful-react'
import Api from './Api'
import useMediaSelectionActions from '../actions/useMediaSelectionActions'
import { speakerDetectionManager } from './SpeakerDetectionManager'
import { videoSubscriptionManager } from './VideoSubscriptionManager'
import { sessionViewModeState } from '@/pages/SessionPage/internal/shared/useViewMode'
import useLogout from '@/app-services/useLogout'
import { collapsibleGroupManager } from '@/UIComponents2/useCollapsibleGroup'
import { resetCloudRecordingState } from './useCloudRecording'
import _ from 'lodash'

const log = logger.createChildLogger({ name: 'useRoomActions' })

export const joinRoomState: {
    joiningRoom: boolean
    joinTimeoutTimer: number | undefined
} = {
    joiningRoom: false,
    joinTimeoutTimer: undefined,
}

/**
 * @description Provides functions to actions that can be applied to the <RoomContext>.
 * This hook should not contain effects or local state.
 */
export default function useRoomActions() {
    const history = useHistory()

    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { setMediaSelection } = useMediaSelectionActions()

    const { show: showSnackbar } = useRootSnackbar()
    const { logUserActivityWithContext } = useAnalytics()

    const { mutate: createRoom, loading } = useMutate({ verb: 'POST', path: `/v1/rooms` })

    const { mutate: updateRoomApi, loading: updateRoomApiLoading } = useMutate({
        verb: 'PATCH',
        path: `/v1/rooms/${roomState.roomUrn || roomState.preRoomUrn || ''}`,
    })

    const { logout } = useLogout()

    const sendMessageToPeer = useCallback(
        (sessionId: string, obj: any, options?: { noStringify?: boolean }) => {
            let objToLog = obj
            if (options?.noStringify) {
                objToLog = `<noStringify option is on so not stringified>`
            }
            log.isSilly() &&
                log.silly(`sendMessageToPeer`, `start. sending to sessionId="${sessionId}" obj=`, { obj: objToLog })

            const peer = roomStateRef.current.peers[sessionId]
            if (!peer) {
                log.error(`sendMessageToPeer`, `peer with sessionId="${sessionId}" does not exist. no-op.`, {
                    obj,
                    options,
                })
                return
            }

            if (roomStateRef.current.expectedNetworkTopology === 'p2p') {
                const p2pConnection = peer.p2pConnection
                if (!p2pConnection) {
                    log.error(
                        `sendMessageToPeer`,
                        `(sessionId="${sessionId}") current topology is p2p but peer.p2pConnection ` +
                            `does not exist. no-op.`,
                        { obj, options }
                    )
                    return
                }

                try {
                    log.isSilly() &&
                        log.silly(`sendMessageToPeer`, `(sessionId="${sessionId}") calling p2pConnection.send().`, {
                            'p2pConnection.connected': p2pConnection.connected,
                        })
                    if (!options?.noStringify) {
                        obj = JSON.stringify(obj)
                    }
                    p2pConnection.send(obj)
                } catch (e) {
                    log.error(`sendMessageToPeer`, `(sessionId="${sessionId}") p2pConnection.send() threw an error:`, {
                        e,
                        obj,
                        options,
                    })
                }
                // let replyTimer: NodeJS.Timeout | undefined = undefined
                // const replyListener = (chunk: any) => {
                //     clearReplyListener()

                //     let data;
                //     try {
                //         data = JSON.parse(chunk)
                //     } catch(e) {
                //     }
                // }
                // const clearReplyListener = () => {
                //     clearTimeout(replyTimer)
                //     p2pConnection.removeListener('data', replyListener)
                // }
                // p2pConnection.addListener('data', replyListener)
                // replyTimer = setTimeout(clearReplyListener, 5000)
            } else {
                const sfuRoom = roomStateRef.current.sfuRoom
                if (!sfuRoom) {
                    log.error(`sendMessageToPeer`, `(sessionId="${sessionId}") not sending because sfuRoom is not set.`)
                    return
                }

                let participant: RemoteParticipant | null = null
                for (let [_, p] of sfuRoom.remoteParticipants) {
                    if (p.identity === sessionId) {
                        participant = p
                        break
                    }
                }

                if (!participant) {
                    log.error(
                        `sendMessageToPeer`,
                        `(sessionId="${sessionId}") not sending because participant not found.`
                    )
                    return
                }

                let data
                if (!options?.noStringify) {
                    // usually, we just stringify the js object & encode into utf-8 Uint8Array.
                    const encoder = new TextEncoder()
                    data = encoder.encode(JSON.stringify(obj))
                } else {
                    if (obj instanceof ArrayBuffer) {
                        // for binary data, if ArrayBuffer, convert to Uint8Array.
                        data = new Uint8Array(obj)
                    } else if (obj instanceof Uint8Array) {
                        // if already is Uint8Array.
                        data = obj
                    } else {
                        log.assert(
                            `sendMessageToPeer`,
                            `noStringify option is set but provided data to send is neither an ` +
                                `ArrayBuffer nor a Uint8Array! No-op.`
                        )
                        return
                    }
                }

                // recall that at this point, `obj` _must_ be a Uint8Array as publishData() only takes in a Uint8Array.

                try {
                    // log.verbose(`sendMessageToPeer`, `(sessionId="${sessionId}") calling publishData().`, {
                    //     dataChannelsInfo: sfuRoom.localParticipant.dataChannelsInfo()
                    // })

                    sfuRoom.localParticipant
                        .publishData(data, {
                            reliable: true,
                            destinationIdentities: [participant.identity],
                        })
                        .then(() => {
                            log.isSilly() &&
                                log.silly(
                                    `sendMessageToPeer`,
                                    `(sessionId="${sessionId}") publishData() successfully returned.`
                                )
                        })
                        .catch((e) => {
                            log.error(`sendMessageToPeer`, `(sessionId="${sessionId}") publishData() threw an error:`, {
                                e,
                            })
                        })
                } catch (e) {
                    log.error(`sendMessageToPeer`, `(sessionId="${sessionId}") publishData() threw an error:`, { e })
                }
            }
        },
        [roomStateRef]
    )

    const broadcastMessage = useCallback(
        (obj: any) => {
            log.isVerbose() && log.verbose(`broadcastMessage`, `start. obj=`, { obj })
            const peers = roomStateRef.current.peers

            if (roomStateRef.current.expectedNetworkTopology === 'p2p') {
                for (let sessionId of Object.keys(peers)) {
                    const peer = peers[sessionId]

                    const p2pConnection = peer.p2pConnection
                    if (!p2pConnection) {
                        log.error(
                            `broadcastMessage`,
                            `(sessionId="${sessionId}") current topology is p2p but peer.p2pConnection ` +
                                `does not exist. no-op.`
                        )
                        return
                    }

                    try {
                        log.isVerbose() &&
                            log.verbose(`broadcastMessage`, `(sessionId="${sessionId}") calling p2pConnection.send().`)
                        p2pConnection.send(JSON.stringify(obj))
                    } catch (e) {
                        log.error(
                            `sendMessageToPeerr`,
                            `(sessionId="${sessionId}") p2pConnection.send() threw an error:`,
                            { e }
                        )
                    }
                }
            } else {
                const sfuRoom = roomStateRef.current.sfuRoom
                if (!sfuRoom) {
                    return
                }

                const encoder = new TextEncoder()

                // publishData takes in a Uint8Array, so we need to convert it
                const data = encoder.encode(JSON.stringify(obj))

                try {
                    sfuRoom.localParticipant.publishData(data, { reliable: true }).catch((e) => {})
                } catch (e) {}
            }
        },
        [roomStateRef]
    )

    const loadRoom = useCallback(async (doc: ApiRoomDocument, extra?: { joinLinkMetadata: any }) => {
        roomDispatch({
            type: 'set_room_base_info',
            value: {
                roomUrn: '',
                sessionId: '',
                preRoomUrn: doc._id,
                joinCode: doc.joinCode,
                joinLink: doc.joinLink,
                customJoinCode: doc.customJoinCode,
                customJoinLink: createCustomJoinLink(doc.customJoinCode),
                anonymousId: doc.anonymousId || '',
                password: doc.password || null,
                name: doc.name,
                host: doc.host,
                sessionStartedTime: null,
                permanent: doc.permanent,
                resetAfterSession: doc.resetAfterSession,
                isSubroomsEnabled: doc.isSubroomsEnabled,
                isListeningSession: doc.isListeningSession,
                allowUserAutoJoin: doc.allowUserAutoJoin,
                allParticipantsJoinMuted: doc.allParticipantsJoinMuted,
                parentRoomUrn: doc.parentRoomUrn || null,
                parentRoom: doc.parentRoom || null,
                subrooms: doc.subrooms || [],
                isConferenceMode: doc.isConferenceMode,
                autoRecordSessions: doc.autoRecordSessions ?? false,
                suppressSessionFeedback: doc.suppressSessionFeedback,
                org: doc.org || null,
                roomTierPolicy: doc.roomTierPolicy,
                joinLinkMetadata: extra?.joinLinkMetadata || null,
                assignedUsers: doc.assignedUsers ?? [],
                isUserSubroomAssignmentActive: doc.isUserSubroomAssignmentActive ?? false,
                isUserSubroomAutoAssigned: doc.isUserSubroomAutoAssigned ?? false,
            },
        })

        await sleep(10)

        for (let i = 0; i < 50; ++i) {
            if (roomStateRef.current.preRoomUrn === doc._id) {
                break
            }
            await sleep(100)
        }
    }, [])

    /**
     * @description Initiates joining the room, making calls to the server
     * and setting up internal states.
     */
    const joinRoom = useCallback(
        async (roomUrn: string, options?: { createNewSessionId: boolean }) => {
            if (!roomUrn) {
                throw new Error('ASSERTION: roomUrn must be set in a call to joinRoom()!')
            }
            if (joinRoomState.joiningRoom) {
                return
            }
            joinRoomState.joiningRoom = true

            // block until `App::loadUser() has completed. This code path may be called before
            // `App::loadUser()` has completed, especially in browser session where the CollaboratePage
            // loading may be in a race condition with creating the guest user.
            if (!userStateRef.current.appSerialId) {
                let timedOut = true
                for (let i = 0; i < 50; ++i) {
                    if (userStateRef.current.appSerialId) {
                        timedOut = false
                        break
                    }
                    await sleep(100)
                }

                if (timedOut) {
                    log.error('load', 'timed out while getting appSerialId.')
                    // just continue on without appSerialId -- better than nothing.
                }
            }

            const { authToken, appSerialId, userUrn, profile, isGuest } = userStateRef.current
            const username = profile?.username || ''
            const displayName = profile?.displayName

            let sessionId = userStateRef.current.sessionId
            if (options?.createNewSessionId) {
                sessionId = uuidv4()
                dispatch({
                    type: 'set_app_ids',
                    value: {
                        sessionId: sessionId,
                        appSerialId: appSerialId,
                    },
                })

                // block until the change is propagated.
                let timedOut = true
                for (let i = 0; i < 50; ++i) {
                    await sleep(100)
                    if (userStateRef.current.sessionId === sessionId) {
                        timedOut = false
                        break
                    }
                }

                if (timedOut) {
                    log.error(`joinRoom`, `failed to set sessionId to "${sessionId}". `, {
                        userState_sessionId: userStateRef.current.sessionId,
                        userState_appSerialId: appSerialId,
                    })

                    sessionId = userStateRef.current.sessionId
                }
            }

            const appVersion = stripSemverVersion(env.APP_VERSION)
            const userGeoData = userStateRef.current.profile?.geoData || null
            log.checkpoint(`call_join_room`, ``, {
                appSerialId: appSerialId,
                userAgent: window.navigator.userAgent,
                sessionId: sessionId,
                roomUrn: roomUrn,
                username: username,
                displayName: displayName,
                isGuest: isGuest,
                userUrn: userUrn,
                appVersion,
                userGeoData,
                sfuNodes: sfuNodeSelection.getNodes(),
                createNewSessionId: options?.createNewSessionId ?? null,
                supportsLivekitMetadataManifest: true,
            })

            const payload: any = {
                authToken: authToken,
                appSerialId: appSerialId,
                sessionId: sessionId,
                userUrn: userUrn,
                username: username,
                displayName: displayName,
                isGuest: isGuest,
                roomUrn: roomUrn,
                appVersion,
                clientKind: Constants.clientKind,
                clientType: userStateRef.current.clientType,
            }

            const metadata = roomState.joinLinkMetadata
            if (metadata) {
                payload['joinLinkMetadata'] = metadata
            }

            wsConnection.sendMessage(
                {
                    type: 'join-room',
                    data: payload,
                },
                {
                    // if this join-room call fails, then the WSConnectionManager `onopen` event handler
                    // which is defined in RoomController::roomControllerWsMessageRouter
                    // will retry it.
                    isReliable: false,
                }
            )

            clearTimeout(joinRoomState.joinTimeoutTimer)
            joinRoomState.joinTimeoutTimer = window.setTimeout(() => {
                log.checkpoint(`join_room_failed:server_timed_out`)
                const msg =
                    'Failed to join the room because the server did not respond. ' +
                    'Please check your internet connection and try again.'
                leaveRoom({ endSession: false, showDialogWithMessage: msg, suppressSessionFeedback: true })
            }, 8000)

            if (roomStateRef.current.roomUrn !== roomUrn) {
                const myAudioMuteState = { ...roomStateRef.current.myAudioMuteState }
                if (roomStateRef.current.allParticipantsJoinMuted) {
                    userStateRef.current.mediaSelection.audioInputs.forEach((a) => {
                        a.settings.channelSelection.forEach((chgrp) => {
                            myAudioMuteState[`${a.deviceId}?chgrp=${JSON.stringify(chgrp)}`] = true
                        })
                    })
                }

                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        roomUrn,
                        preRoomUrn: '',
                        joinRoomCallStartedTime: new Date(),
                        myAudioMuteState: myAudioMuteState,
                    },
                })
            }
            joinRoomState.joiningRoom = false
        },
        [dispatch, roomDispatch, roomStateRef, userStateRef]
    )

    const destroyRoom = useCallback(() => {
        // TODO:
        // this.myMidiInputDevice?.close()

        // Revert to non-sticky window.
        ElectronIPC.setWindowOptions({ isAlwaysOnTop: false })

        ElectronIPC.setPowerSaveBlocker(false)

        try {
            if (roomStateRef.current.sfuRoom) {
                roomStateRef.current.sfuRoom.disconnect()
            }
        } catch (e) {}

        unstable_batchedUpdates(() => {
            dispatch({
                type: 'clear_media_selection_and_streams',
                value: null,
            })
            roomDispatch({
                type: 'update_room_context',
                value: loadInitialRoomContext(),
            })
            appDispatch({
                type: 'update_app_state',
                value: { isFullscreen: false },
            })
        })
        sessionViewModeState.exitFullscreen()
        if (sessionViewModeState.mode.galleryHidden) {
            sessionViewModeState.setGalleryHidden(false)
        }
        collapsibleGroupManager.reset()
        resetCloudRecordingState()
    }, [appDispatch, dispatch, roomDispatch])

    const leaveRoom = useCallback(
        async ({
            endSession,
            showDialogWithMessage,
            showSnackbarWithMessage,
            stayInCollaboratePage,
            stayInCollaboratePageForSwitchRoom,
            suppressSessionFeedback,
        }: {
            endSession: boolean
            showDialogWithMessage?: string
            showSnackbarWithMessage?: string
            stayInCollaboratePage?: boolean
            stayInCollaboratePageForSwitchRoom?: boolean
            suppressSessionFeedback?: boolean
        }) => {
            try {
                const roomUrn = roomStateRef.current.roomUrn
                const sessionId = roomStateRef.current.sessionId
                const intent = endSession ? 'leave_room_by_user_request' : 'leave_room_by_user_request_and_end_session'

                console.log(`CollaboratePage: leaveSessionHandler: calling session.leaveRoom().`)

                log.checkpoint('leave_room', '', { intent: intent })

                logger.flushLogs()

                if (!(stayInCollaboratePage || stayInCollaboratePageForSwitchRoom)) {
                    roomDispatch({
                        type: 'update_room_context',
                        value: { isLeaving: true },
                    })
                }

                // Tell the server that we're leaving
                wsConnection.sendMessage({
                    type: 'leave-room',
                    data: {
                        authToken: userStateRef.current.authToken,
                        sessionId: userStateRef.current.sessionId,
                        userUrn: userStateRef.current.userUrn,
                        roomUrn: roomStateRef.current.roomUrn,
                        endSession: endSession,
                    },
                })
                if (endSession) {
                    logUserActivityWithContext(AnalyticsEvents.HOST_ENDED_SESSION)
                } else {
                    logUserActivityWithContext(AnalyticsEvents.PEER_LEFT)
                }
                if (roomStateRef.current.isScreenRecording) {
                    screenRecordingCommand('stop')
                }

                // hack: wait for 'leave-room' to be sent to the server and the disconnect_preamble
                // to be propagated to the peers.
                if (!stayInCollaboratePageForSwitchRoom) {
                    await sleep(500)
                }

                try {
                    const noStopTracks = stayInCollaboratePage || stayInCollaboratePageForSwitchRoom

                    if (!noStopTracks) {
                        userStateRef.current.mediaStreams.audioInputs.forEach((a) => {
                            try {
                                a.stop()
                            } catch (e) {}
                        })
                    }

                    if (roomStateRef.current.sfuRoom) {
                        roomStateRef.current.sfuRoom.disconnect(!noStopTracks)
                    }
                } catch (e) {}

                videoSubscriptionManager.clear()
                speakerDetectionManager.stopDetection()

                if (showDialogWithMessage) {
                    appDispatch({
                        type: 'new_message_dialog',
                        value: {
                            id: 'room_exit_error_dialog',
                            text: showDialogWithMessage,
                        },
                    })
                }

                if (showSnackbarWithMessage) {
                    showSnackbar({
                        severity: 'info',
                        message: showSnackbarWithMessage,
                        duration: 5000,
                    })
                }

                if (!stayInCollaboratePage) {
                    unstable_batchedUpdates(() => {
                        // clean up app state
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showUpgradeToMuseProDialog: null,
                                showSessionIsBoostedDialog: false,
                                showChooseVirtualBackgroundModal: false,
                            },
                        })

                        if (!stayInCollaboratePageForSwitchRoom) {
                            roomDispatch({
                                type: 'update_room_context',
                                value: { ...loadInitialRoomContext() },
                            })
                        } else {
                            roomDispatch({
                                type: 'update_room_context',
                                value: {
                                    roomUrn: '',
                                    myWaitlistStatus: 'isJoining',
                                    roomWaitlist: [],
                                    peers: {},
                                    pinnedPeerId: null,
                                    sfuRoomConnected: false,
                                    screenControlRequest: { reqId: 0, receiverSessionId: '', op: '' },
                                    lastScreenControlRequestResponse: { reqId: 0, requesterSessionId: '', status: '' },
                                    screenControlRequestResponseHistory: [],
                                    isShowingWaitingRoomPopup: false,
                                },
                            })
                        }
                    })

                    if (!stayInCollaboratePageForSwitchRoom) {
                        const suppressFeedback =
                            (suppressSessionFeedback ?? false) || roomStateRef.current.suppressSessionFeedback
                        if (userStateRef.current.isGuest) {
                            logout()
                        } else if (suppressFeedback) {
                            history.push(`/`)
                        } else {
                            history.push(`/?feedback=${roomUrn ?? ''}:${sessionId ?? ''}`)
                        }
                    }
                }
            } catch (e) {
                console.error(e)
            }
        },
        [history, roomStateRef, userStateRef]
    )

    const softDisconnectRoom = useCallback(() => {
        const noStopTracks = true
        if (roomStateRef.current.sfuRoom) {
            roomStateRef.current.sfuRoom.disconnect(!noStopTracks)
        }

        roomDispatch({
            type: 'update_room_context',
            value: {
                roomUrn: '',
                peers: {},
            },
        })
    }, [])

    const switchRoom = useCallback(async (subroomUrn: string) => {
        try {
            roomDispatch({
                type: 'update_room_context',
                value: { isSwitchingRoom: subroomUrn },
            })

            setMediaSelection({ desktop: '_none' })

            // If we are switching to the parent room, then the password will get used,
            // otherwise it will be ignored. (recall that subrooms don't have passwords.)
            const password = roomStateRef.current.parentRoom?.password || undefined

            // GET subroom from API, then dispatch
            const room = await Api.getRoom(subroomUrn, password)
            if (!room) {
                roomDispatch({
                    type: 'update_room_context',
                    value: { isSwitchingRoom: null },
                })
                showSnackbar({
                    severity: 'error',
                    message: 'Could not join room: failed to get data.',
                })
                return false
            }

            // for switching rooms, disconnect() first with `stopTracks = false`, otherwise
            // our local media streams may get stopped by livekit on call to ws 'leave-room'.
            if (roomStateRef.current.sfuRoom) {
                const stopTracks = false
                roomStateRef.current.sfuRoom.disconnect(stopTracks)
            }

            await leaveRoom({ endSession: false, stayInCollaboratePageForSwitchRoom: true })

            await loadRoom(room)

            if (history.location.pathname === '/pages/host-session-settings') {
                // actually go to the CollaboratePage
                history.push('/pages/collaborate')
            } else {
                await joinRoom(subroomUrn)
            }

            return true
        } catch (e) {
            roomDispatch({
                type: 'update_room_context',
                value: { isSwitchingRoom: null },
            })
            showSnackbar({
                severity: 'error',
                message: `Could not join room: "${(e as Error)?.message}".`,
            })
            return false
        }
    }, [])

    // stub
    const updateRoomData = useCallback(async (roomUrn: string) => {
        const room = await Api.getRoom(roomUrn)
        if (!room) {
            return false
        }

        roomDispatch({
            type: 'update_room_context',
            value: {
                parentRoomUrn: room.parentRoomUrn || null,
                subrooms: room.subrooms || [],
            },
        })
        return true
    }, [])

    const upgradeToSfuRoom = useCallback(() => {
        // we tell the server to upgrade the room to sfu.
        wsConnection.sendMessage({
            type: 'set-room-metadata',
            data: {
                sessionId: userStateRef.current.sessionId,
                roomUrn: roomStateRef.current.roomUrn,
                metadata: {
                    networkTopology: 'sfu',
                },
            },
        })
    }, [roomStateRef, userStateRef])

    const boostRoom = async (reason: UpgradeToMuseProDialogType) => {
        roomDispatch({
            type: 'update_room_context',
            value: { isBoostBeingApplied: true },
        })

        const reply = await wsConnection.sendMessage(
            {
                type: 'boost-room',
                data: {
                    authToken: userStateRef.current.authToken,
                    appSerialId: userStateRef.current.appSerialId,
                    appVersion: stripSemverVersion(env.APP_VERSION),
                    sessionId: userStateRef.current.sessionId,
                    userUrn: userStateRef.current.userUrn,
                    username: userStateRef.current.profile?.username || '',
                    roomUrn: roomStateRef.current.roomUrn || roomStateRef.current.preRoomUrn || '',
                    reason: reason,
                },
            },
            { isReliable: true, checkAck: true, totalTimeoutMs: 5000 }
        )

        roomDispatch({
            type: 'update_room_context',
            value: { isBoostBeingApplied: false },
        })

        const boostsRemaining = userStateRef.current.profile?.boostsRemaining ?? 0
        const boostsRemainingNext = boostsRemaining ? boostsRemaining - 1 : 0
        dispatch({
            type: 'update_user_profile',
            value: {
                boostsRemaining: boostsRemainingNext,
            },
        })

        if (reply.error) {
            // 'room_not_found' | 'user_not_found' | 'boost_limit_reached' | 'internal_error'
            showSnackbar({
                severity: 'error',
                message: `Failed to apply boost! Error code is "${reply.error.code}".`,
            })
        } else {
            if (reply.sessionState || reply.roomTierPolicy) {
                const sessionState = toSessionState(reply.sessionState)
                const roomTierPolicy = reply.roomTierPolicy

                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        ...(sessionState ? { sessionState } : {}),
                        ...(roomTierPolicy ? { roomTierPolicy } : {}),
                    },
                })
            }

            if (appStateRef.current.appSettings.doNotShowRoomBoostedDialog) {
                showSnackbar({
                    severity: 'info',
                    message: 'Boost applied!',
                })
            } else {
                appDispatch({
                    type: 'update_app_state',
                    value: { showSessionIsBoostedDialog: true },
                })
            }

            if (roomStateRef.current.myWaitlistStatus === 'isFullSession') {
                // try to join room again after boost.
                joinRoom(roomStateRef.current.roomUrn)
            }
        }
    }

    const startCloudRecording = useCallback(
        async (sfuRoomInfo?: { nearestNode: SfuNode }): Promise<boolean> => {
            if (!sfuRoomInfo && roomStateRef.current.sfuRoomInfo) {
                sfuRoomInfo = roomStateRef.current.sfuRoomInfo
            }

            const sfuServer = sfuRoomInfo?.nearestNode?.url
            if (!sfuServer) {
                showSnackbar({ severity: 'error', message: 'Failed to start cloud recording!' })
                log.assert('startCloudRecording', 'sfuServer is null!')
                return false
            }

            wsConnection.sendMessage(
                {
                    type: 'start-sfu-recording',
                    data: {
                        authToken: userStateRef.current.authToken,
                        roomUrn: roomStateRef.current.roomUrn,
                        userUrn: userStateRef.current.userUrn,
                        identity: userStateRef.current.sessionId,
                        sfuServer,
                    },
                },
                {
                    isReliable: true,
                    checkAck: true,
                }
            )

            return true
        },
        [roomStateRef, userStateRef]
    )

    const sendRoomUserControlMessageToServer = useCallback(
        async (code: 'accept' | 'deny' | 'remove', sessionId: string) => {
            const msg = {
                type: 'room-user-control',
                data: {
                    authToken: userStateRef.current.authToken,
                    userUrn: userStateRef.current.userUrn,
                    sessionId: userStateRef.current.sessionId,
                    roomUrn: roomStateRef.current.roomUrn,
                    waitlistAccept: code === 'accept' ? sessionId : undefined,
                    waitlistDeny: code === 'deny' ? sessionId : undefined,
                    removeUser: code === 'remove' ? sessionId : undefined,
                },
            }

            const response = await wsConnection.sendMessage(msg, {
                isReliable: true,
                checkAck: true,
                totalTimeoutMs: 5000,
                tBetweenRetriesMs: 2500,
            })
            if (response instanceof WSConnectionSendError) {
                // timed out
                const roomWaitlistNext = [...roomStateRef.current.roomWaitlist]
                const item = roomWaitlistNext.find((a) => a.sessionId === sessionId)
                if (item) {
                    item.isBeingJoinedIn = false
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            roomWaitlist: roomWaitlistNext,
                        },
                    })
                }
            } else if (response?.error) {
                // error - likely the peer is not there.
                const code = response.error.code || ''
                if (code === 'peer_not_found') {
                    appDispatch({
                        type: 'new_message_dialog',
                        value: {
                            id: 'room-user-control-accept-error',
                            text: 'Looks like the user is no longer in the room.',
                        },
                    })
                } else {
                    appDispatch({
                        type: 'new_message_dialog',
                        value: {
                            id: 'room-user-control-accept-error',
                            text: `An error occurred while accepting the user: code="${code}"`,
                        },
                    })
                }

                const roomWaitlistNext = roomStateRef.current.roomWaitlist.filter((a) => a.sessionId !== sessionId)
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        roomWaitlist: roomWaitlistNext,
                    },
                })
            }
        },
        [appDispatch, roomDispatch, roomStateRef, userStateRef]
    )

    const createSubroom = useCallback(async (data: { subroomName: string; collaborators: string[] }) => {
        const parentRoomUrn =
            roomStateRef.current.parentRoomUrn || roomStateRef.current.roomUrn || roomStateRef.current.preRoomUrn
        if (!parentRoomUrn) {
            return
        }

        const shouldUpgradeToSfu = roomStateRef.current.expectedNetworkTopology === 'p2p'
        const subroomName = data.subroomName

        const subroomsOptimistic = [...roomStateRef.current.subrooms]
        subroomsOptimistic.push({
            _id: '',
            name: subroomName,
        })
        roomDispatch({
            type: 'update_room_context',
            value: {
                subrooms: subroomsOptimistic,
            },
        })

        const result = await createRoom({
            name: subroomName,
            parentRoomUrn,
            collaborators: [],
            allowJoiningFromCode: true,
            createdFromAppVersion: stripSemverVersion(env.APP_VERSION),
        })

        if (!result.room) {
            // TODO: error
            return
        }

        const subroomsNext = roomStateRef.current.subrooms.filter((a) => a._id !== '') // remove optimistic updates
        for (let room of result.room.subrooms || []) {
            if (!subroomsNext.find((r) => r._id === room._id)) {
                subroomsNext.push(room)
            }
        }

        if (shouldUpgradeToSfu) {
            upgradeToSfuRoom()
        }

        // if root room, then set allowUserAutoJoin to true.
        if (!roomStateRef.current.parentRoomUrn) {
            if (roomStateRef.current.allowUserAutoJoin !== true) {
                updateRoomApi({ allowUserAutoJoin: true })
            }
        }

        roomDispatch({
            type: 'update_room_context',
            value: {
                subrooms: subroomsNext,
                allowUserAutoJoin: true, // policy
            },
        })
    }, [])

    const boostRoomToPro = async () => {
        log.checkpoint(
            'Sending Tier Upgrade',
            JSON.stringify({
                authToken: userStateRef.current.authToken,
                appSerialId: userStateRef.current.appSerialId,
                appVersion: stripSemverVersion(env.APP_VERSION),
                sessionId: userStateRef.current.sessionId,
                userUrn: userStateRef.current.userUrn,
                username: userStateRef.current.profile?.username || '',
                roomUrn: roomStateRef.current.roomUrn || '',
            })
        )

        const reply = await wsConnection.sendMessage(
            {
                type: 'tier-update',
                data: {
                    authToken: userStateRef.current.authToken,
                    appSerialId: userStateRef.current.appSerialId,
                    appVersion: stripSemverVersion(env.APP_VERSION),
                    sessionId: userStateRef.current.sessionId,
                    userUrn: userStateRef.current.userUrn,
                    username: userStateRef.current.profile?.username || '',
                    roomUrn: roomStateRef.current.roomUrn || '',
                },
            },
            { isReliable: true, checkAck: true, totalTimeoutMs: 5000 }
        )

        if (!reply.error) {
            if (roomStateRef.current.roomUrn && reply.roomTierPolicy) {
                log.checkpoint(`Upgrading room tier for: ${roomStateRef.current.roomUrn}`, JSON.stringify(reply))
                const sessionState = toSessionState(reply.sessionState)
                const roomTierPolicy = reply.roomTierPolicy

                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        ...(sessionState ? { sessionState } : {}),
                        ...(roomTierPolicy ? { roomTierPolicy } : {}),
                    },
                })
            } else {
                log.checkpoint(`No room found to upgrade`, JSON.stringify(reply))
            }

            appDispatch({
                type: 'update_app_state',
                value: { showAccountIsUpgradedDialog: true },
            })
            return reply.roomTierPolicy
        }
        return null
    }

    const handleWaitlistUser = useCallback(
        (waitlistItem: { appSerialId: string; sessionId: string }, isAccepted: boolean) => {
            let roomWaitlistNext: WaitlistItem[]
            if (isAccepted) {
                roomWaitlistNext = [...roomStateRef.current.roomWaitlist]
                roomWaitlistNext
                    .filter((p) => p.appSerialId === waitlistItem.appSerialId)
                    .forEach((item) => {
                        item.isBeingJoinedIn = true
                    })
            } else {
                roomWaitlistNext = roomStateRef.current.roomWaitlist.filter(
                    (p) => p.appSerialId !== waitlistItem.appSerialId
                )
            }

            sendRoomUserControlMessageToServer(isAccepted ? 'accept' : 'deny', waitlistItem.sessionId)

            roomDispatch({
                type: 'update_room_context',
                value: { roomWaitlist: roomWaitlistNext },
            })
        },
        [roomDispatch, roomStateRef, sendRoomUserControlMessageToServer]
    )

    const startRemovePeer = useCallback(
        (sessionId: string) => {
            const peer = roomStateRef.current.peers[sessionId]
            if (peer) {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        isRemoveUserConfirmationDialogOpen: {
                            sessionId: sessionId,
                            username: peer.username,
                        },
                    },
                })
            }
        },
        [roomDispatch, roomStateRef]
    )

    const removePeer = useCallback(
        (sessionId: string | null) => {
            let nextPeers: { [x: string]: MusePeer } | null = null
            if (sessionId) {
                sendRoomUserControlMessageToServer('remove', sessionId)

                const peerToRemove = roomStateRef.current.peers[sessionId]
                if (peerToRemove) {
                    /// peerToRemove.p2pConnection && peerToRemove.p2pConnection.destroy()
                    /// peerToRemove.sfuConnection && peerToRemove.sfuConnection.disconnect()

                    const next = { ...roomStateRef.current.peers }
                    delete next[peerToRemove.sessionId]
                    nextPeers = next
                    logUserActivityWithContext(AnalyticsEvents.PEER_REMOVED)
                }
            }

            // TODO: perhaps a more sophisticated way to remove the peer than downright
            // removing the entry in peers[].
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isRemoveUserConfirmationDialogOpen: null,
                    ...(nextPeers ? { peers: nextPeers } : {}),
                },
            })
        },
        [roomDispatch, roomStateRef, sendRoomUserControlMessageToServer]
    )

    const foreachRemoteAudioDevices = useCallback(
        (
            peers: { [key: string]: MusePeer },
            cbk: (
                peer: MusePeer,
                media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio,
                deviceId: string
            ) => void
        ) => {
            Object.keys(peers).forEach((sessionId) => {
                const peer = peers[sessionId]
                foreachRemoteAudioDevicesOfPeer(peer, cbk)
            })
        },
        []
    )

    const foreachRemoteAudioDevicesOfPeer = useCallback(
        (
            peer: MusePeer,
            cbk: (
                peer: MusePeer,
                media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio,
                deviceId: string
            ) => void
        ) => {
            peer.remoteAudios.forEach((media) => {
                let chgrps: number[][] = []

                if (media.info.channelSelection) {
                    chgrps = media.info.channelSelection
                } else {
                    // backward compatibility
                    // @ts-ignore
                    let channelCt = media.info.reportedChannelCount || media.info.channelCount || 2
                    let a: number[] = []
                    for (let i = 0; i < channelCt; ++i) {
                        a.push(i)
                    }
                    chgrps.push(a)
                }

                for (let chgrp of chgrps) {
                    const extDeviceId = encodeExtendedDeviceId(media.info.deviceId, chgrp)
                    cbk(peer, media, extDeviceId)
                }
            })
        },
        []
    )

    const toggleMuteAllPeers = useCallback(() => {
        const nextMuteState = !calcIsRemoteAudioDeviceAllMuted(null /*all*/)

        const dispatches: {
            sessionId: string
            peer: Partial<MusePeer>
        }[] = []

        Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
            const d = toggleRemoteAudioMuteCalcDelta(roomStateRef.current.peers[sessionId], null, {
                overrideIsMutedNext: nextMuteState,
            })
            dispatches.push({
                sessionId: d.sessionId,
                peer: {
                    remoteAudioContolStates: d.remoteAudioContolStates,
                },
            })
        })

        roomDispatch({
            type: 'update_peer',
            value: dispatches,
        })
    }, [])

    const leaveRoomStart = useCallback(
        (option: { endSession: boolean }) => {
            // Show confirmation modal. The modal will call `leaveSessionHandler()` if user chose affirmative.
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isLeaveConfirmationDialogOpen: {
                        uuid: uuidv4(),
                        ...option,
                    },
                },
            })
        },
        [roomDispatch]
    )

    const toggleMyMidiDeviceMute = useCallback(
        (deviceId: string) => {
            const next = { ...roomStateRef.current.myMidiMuteState }
            next[deviceId] = !(roomStateRef.current.myMidiMuteState[deviceId] || false)

            roomDispatch({
                type: 'update_room_context',
                value: {
                    myMidiMuteState: next,
                },
            })
        },
        [roomDispatch, roomStateRef]
    )

    const calcIsMyAudioDeviceAllMuted = useCallback(() => {
        const allStreamIds = state.mediaStreams.audioInputs.flatMap((a) => {
            return a.info.channelSelection.map((chgrp) => {
                return `${a.info.deviceId}?chgrp=${JSON.stringify(chgrp)}`
            })
        })
        const mutedStreamIds = Object.keys(roomState.myAudioMuteState).filter(
            (deviceId) => roomState.myAudioMuteState[deviceId] === true
        )

        for (let a of allStreamIds) {
            if (!mutedStreamIds.includes(a)) {
                return false
            }
        }
        return true
    }, [roomState.myAudioMuteState, state.mediaStreams.audioInputs])

    const toggleMyAudioDeviceMute = useCallback(
        (deviceId?: string) => {
            let next: { [x: string]: boolean } = {}
            if (deviceId) {
                const isCurrentlyMuted = roomStateRef.current.myAudioMuteState[deviceId]
                next = {
                    ...roomStateRef.current.myAudioMuteState,
                    [deviceId]: !isCurrentlyMuted,
                }
            } else {
                const b = !calcIsMyAudioDeviceAllMuted()
                next = {}
                state.mediaStreams.audioInputs.forEach((a) => {
                    a.info.channelSelection.forEach((chgrp) => {
                        next[`${a.info.deviceId}?chgrp=${JSON.stringify(chgrp)}`] = b
                    })
                })
            }

            roomDispatch({
                type: 'update_room_context',
                value: {
                    myAudioMuteState: next,
                },
            })
        },
        [calcIsMyAudioDeviceAllMuted, roomDispatch, roomStateRef, state.mediaStreams.audioInputs]
    )

    const myAudioDeviceMuteAll = useCallback(() => {
        let next: { [x: string]: boolean } = {}
        userStateRef.current.mediaStreams.audioInputs.forEach((a) => {
            a.info.channelSelection.forEach((chgrp) => {
                next[`${a.info.deviceId}?chgrp=${JSON.stringify(chgrp)}`] = true
            })
        })

        roomDispatch({
            type: 'update_room_context',
            value: {
                myAudioMuteState: next,
            },
        })
    }, [])

    const toggleMyAudioDeviceTalkback = useCallback(
        (deviceId: string) => {
            const isTalkbackEnabledNext = !roomState.myAudioTalkbackState[deviceId]
            let nextValue: Partial<RoomContextState> = {
                myAudioTalkbackState: {
                    ...roomState.myAudioTalkbackState,
                    [deviceId]: isTalkbackEnabledNext,
                },
            }

            // mute corresponding track if talkback enabled.
            if (isTalkbackEnabledNext) {
                nextValue.myAudioMuteState = {
                    ...roomStateRef.current.myAudioMuteState,
                    [deviceId]: true,
                }
            }

            if (isTalkbackEnabledNext) {
                log.checkpoint('talkback_enabled', '', { deviceId })
            } else {
                log.checkpoint('talkback_disabled', '', { deviceId })
            }

            roomDispatch({
                type: 'update_room_context',
                value: nextValue,
            })
        },
        [roomDispatch, roomState.myAudioTalkbackState, roomStateRef]
    )

    const toggleAutoMute = useCallback(
        (deviceId: string) => {
            const isAutoMuteEnabledNext = !roomState.autoMuteEnabled[deviceId]
            let nextValue: Partial<RoomContextState> = {
                autoMuteEnabled: {
                    ...roomState.autoMuteEnabled,
                    [deviceId]: isAutoMuteEnabledNext,
                },
            }

            if (isAutoMuteEnabledNext) {
                log.checkpoint('auto_mute_enabled', '', { deviceId })
            } else {
                log.checkpoint('auto_mute_disabled', '', { deviceId })
            }

            roomDispatch({
                type: 'update_room_context',
                value: nextValue,
            })
        },
        [roomDispatch, roomState.autoMuteEnabled, roomStateRef]
    )

    const toggleMyVideoDeviceMute = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: {
                myVideoMuteState: !roomStateRef.current.myVideoMuteState,
            },
        })
        if (roomStateRef.current.myVideoMuteState) {
            setMediaSelection({ video: userStateRef.current.mediaSelection.previousVideo })
        } else {
            setMediaSelection({ previousVideo: userStateRef.current.mediaSelection.video, video: '_none' })
        }
    }, [roomDispatch, roomStateRef, setMediaSelection, userStateRef])

    const isMyVideoDeviceMuted = useCallback(() => {
        return roomState.myVideoMuteState === true
    }, [roomState.myVideoMuteState])

    const openRecordTalkbackKeyDialog = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: {
                isRecordTalkbackKeyDialogOpen: true,
            },
        })
    }, [roomDispatch])

    const closeRecordTalkbackKeyDialog = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: {
                isRecordTalkbackKeyDialogOpen: false,
            },
        })
        ElectronIPC.iohookSetMode('normal')
    }, [roomDispatch])

    const talkbackKeyStateChangeHandler = useCallback(
        (isKeyDown: boolean) => {
            let myAudioMuteStateNext: { [key: string]: boolean } = { ...roomState.myAudioMuteState }
            Object.keys(roomState.myAudioTalkbackState).forEach((deviceId) => {
                if (roomState.myAudioTalkbackState[deviceId] === true) {
                    myAudioMuteStateNext[deviceId] = !isKeyDown // if key down, unmute; if down, mute.
                }
            })
            if (!_.isEqual(myAudioMuteStateNext, roomState.myAudioMuteState)) {
                roomDispatch({
                    type: 'update_room_context',
                    value: {
                        myAudioMuteState: myAudioMuteStateNext,
                    },
                })
            }
        },
        [roomState.myAudioMuteState, roomDispatch, roomStateRef]
    )

    const toggleRemoteVideoMute = useCallback(
        (peer: MusePeer) => {
            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peer.sessionId,
                    peer: {
                        remoteVideoControlStates: {
                            isMuted: !peer.remoteVideoControlStates?.isMuted,
                        },
                    },
                },
            })
        },
        [roomDispatch]
    )

    const calcIsRemoteAudioDeviceAllMuted = useCallback((peer: MusePeer | null) => {
        if (peer) {
            let b = true
            foreachRemoteAudioDevicesOfPeer(peer, (peer, media, extDeviceId) => {
                if (
                    peer.remoteAudioContolStates[extDeviceId]?.isMuted ||
                    peer.remoteAudioContolStates[extDeviceId]?.isMutedBySolo
                ) {
                } else {
                    b = false
                }
            })

            return b
        } else {
            const peers = Object.keys(roomStateRef.current.peers).map(
                (sessionId) => roomStateRef.current.peers[sessionId]
            )
            let c = 0
            for (let peer of peers) {
                if (peer) {
                    if (calcIsRemoteAudioDeviceAllMuted(peer)) {
                        c += 1
                    }
                }
            }

            return c === peers.length
        }
    }, [])

    const isRemoteAudioDeviceAllMutedForAllPeers = useMemo(() => {
        const peers = Object.keys(roomState.peers).map((sessionId) => roomState.peers[sessionId])
        let c = 0
        for (let peer of peers) {
            if (peer) {
                if (calcIsRemoteAudioDeviceAllMuted(peer)) {
                    c += 1
                }
            }
        }

        return c === peers.length
    }, [roomState.peers])

    const calcIsRemoteAudioDeviceAllSoloed = useCallback((peer: MusePeer) => {
        if (peer.remoteAudios.length === 0) {
            return false
        }

        let b = true
        foreachRemoteAudioDevicesOfPeer(peer, (peer, media, extDeviceId) => {
            if (!peer.remoteAudioContolStates[extDeviceId]?.isSoloed) {
                b = false
            }
        })

        return b
    }, [])

    const toggleRemoteAudioMuteCalcDelta = useCallback(
        (peer: MusePeer, deviceId?: string | null, options?: { overrideIsMutedNext?: boolean }) => {
            const next = structuredClone(peer.remoteAudioContolStates)

            if (deviceId) {
                const isMutedNext =
                    options?.overrideIsMutedNext != null
                        ? options.overrideIsMutedNext
                        : !(next[deviceId]?.isMuted || false)

                next[deviceId] = {
                    ...(next[deviceId] || {}),
                    isMuted: isMutedNext,
                }
            } else {
                const isMutedNext =
                    options?.overrideIsMutedNext != null
                        ? options.overrideIsMutedNext
                        : !calcIsRemoteAudioDeviceAllMuted(peer)

                foreachRemoteAudioDevicesOfPeer(peer, (peer, media, extDeviceId) => {
                    next[extDeviceId] = {
                        ...(next[extDeviceId] || {}),
                        isMuted: isMutedNext,
                    }
                })
            }

            return {
                sessionId: peer.sessionId,
                remoteAudioContolStates: next,
            }
        },
        []
    )

    const toggleRemoteAudioMute = useCallback((peer: MusePeer, deviceId?: string | null) => {
        const d = toggleRemoteAudioMuteCalcDelta(peer, deviceId)

        roomDispatch({
            type: 'update_peer',
            value: {
                sessionId: d.sessionId,
                peer: {
                    remoteAudioContolStates: d.remoteAudioContolStates,
                },
            },
        })
    }, [])

    const toggleRemoteAudioSolo = useCallback((peer: MusePeer, deviceId?: string) => {
        const next = structuredClone(peer.remoteAudioContolStates)
        // const isSoloedNext = !(next[deviceId]?.isSoloed || false)

        // next[deviceId] = {
        //     ...(next[deviceId] || {}),
        //     isSoloed: isSoloedNext,
        // }

        if (deviceId) {
            const isSoloedNext = !(next[deviceId]?.isSoloed || false)
            next[deviceId] = {
                ...(next[deviceId] || {}),
                isSoloed: isSoloedNext,
            }
        } else {
            const isSoloedNext = !calcIsRemoteAudioDeviceAllSoloed(peer)
            foreachRemoteAudioDevicesOfPeer(peer, (peer, media, extDeviceId) => {
                next[extDeviceId] = {
                    ...(next[extDeviceId] || {}),
                    isSoloed: isSoloedNext,
                }
            })
        }

        // all the other audio tracks on every peer must be muted
        // unless that track itself is muted.

        roomDispatch({
            type: 'update_peer',
            value: {
                sessionId: peer.sessionId,
                peer: {
                    remoteAudioContolStates: next,
                },
            },
        })
    }, [])

    const changeRemoteAudioVolume = useCallback(
        (peer: MusePeer, deviceId: string, value: number) => {
            sendMessageToPeer(peer.sessionId, {
                type: 'change-audio-volume',
                data: {
                    deviceId,
                    value,
                },
            })
        },
        [sendMessageToPeer]
    )

    const stopRemoteAudioRecord = useCallback(
        (peer: MusePeer, deviceId?: string) => {
            const sessionId = peer.sessionId
            let next = structuredClone(peer.remoteAudioRecordingState)
            if (deviceId) {
                next[deviceId] = { ...(next[deviceId] || {}), isRecording: 'stopping' }
            } else {
                Object.keys(next).forEach((deviceId) => {
                    next[deviceId] = { ...(next[deviceId] || {}), isRecording: 'stopping' }
                })
            }

            // Set throttling timeout.
            setTimeout(() => {
                let next = null
                const _peer = roomStateRef.current.peers[sessionId]
                if (_peer) {
                    if (deviceId) {
                        const isRecording = _peer.remoteAudioRecordingState[deviceId]?.isRecording || false
                        if (isRecording === 'stopping') {
                            next = structuredClone(_peer.remoteAudioRecordingState)
                            next[deviceId] = { ...(next[deviceId] || {}), isRecording: false }
                        }
                    } else {
                        Object.keys(_peer.remoteAudioRecordingState).forEach((deviceId) => {
                            const isRecording = _peer.remoteAudioRecordingState[deviceId]?.isRecording || false
                            if (isRecording === 'stopping') {
                                next = structuredClone(_peer.remoteAudioRecordingState)
                                next[deviceId] = { ...(next[deviceId] || {}), isRecording: false }
                            }
                        })
                    }
                }

                if (next) {
                    roomDispatch({
                        type: 'update_peer',
                        value: {
                            sessionId: sessionId,
                            peer: { remoteAudioRecordingState: next },
                        },
                    })
                }
            }, 2500)

            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peer.sessionId,
                    peer: { remoteAudioRecordingState: next },
                },
            })
        },
        [roomDispatch, roomStateRef]
    )

    const calcCurrRemoteRecordingPeerAudio = useCallback(
        (roomState: RoomContextState, options?: { noIncludeStopping: boolean }) => {
            for (let sessionId of Object.keys(roomState.peers)) {
                const peer = roomState.peers[sessionId]
                for (let deviceId of Object.keys(peer.remoteAudioRecordingState)) {
                    const isRecording: boolean | 'stopping' = peer.remoteAudioRecordingState[deviceId].isRecording
                    if (isRecording === true || (!options?.noIncludeStopping && isRecording === 'stopping')) {
                        const device = peer.remoteAudios.find((a) => a.info.deviceId === deviceId) || null
                        return {
                            device: device,
                            peer: peer,
                        }
                    }
                }
            }
            return null
        },
        []
    )

    const {
        onRecordingTriggered,
        onStartRemoteRecordingHandler,
        onStopRemoteRecordingHandler,
        onRemoteRecordingErrorHandler,
        onRemoteRecordingChunkHandler,
    } = useRemoteRecordingActions(sendMessageToPeer, stopRemoteAudioRecord, calcCurrRemoteRecordingPeerAudio)

    const toggleRemoteAudioRecord = useCallback(
        (peer: MusePeer, deviceId: string, deviceLabel: string) => {
            const sessionId = peer.sessionId
            const isRecording: boolean | 'stopping' = peer.remoteAudioRecordingState[deviceId]?.isRecording || false

            let next = null
            if (isRecording === false) {
                // currently not recording; start recording.
                // but if there is another device getting recorded, then no-op.
                const currRemoteRecording = calcCurrRemoteRecordingPeerAudio(roomStateRef.current)
                if (currRemoteRecording) {
                    // this condition will be disabled from the UI.
                    return
                }

                next = structuredClone(peer.remoteAudioRecordingState)
                next[deviceId] = { ...(next[deviceId] || {}), isRecording: true }

                onRecordingTriggered(sessionId, deviceId, 'start', deviceLabel)

                logger.event({
                    page: 'PeerControlPanel',
                    action: 'remoterecording_start',
                    data: {
                        peerUsername: peer.username,
                        deviceId: deviceId,
                    },
                })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_START_RECORD_DEVICE_BUTTON, {
                    deviceId,
                    deviceLabel,
                    peerUsername: peer.username,
                })
            } else if (isRecording === true) {
                // stop recording.
                stopRemoteAudioRecord(peer, deviceId)
                onRecordingTriggered(sessionId, deviceId, 'stop')

                logger.event({
                    page: 'PeerControlPanel',
                    action: 'remoterecording_stop',
                    data: {
                        peerUsername: peer.username,
                        deviceId: deviceId,
                    },
                })
                logUserActivityWithContext(AnalyticsEvents.CLICKED_STOP_RECORD_DEVICE_BUTTON, {
                    deviceId,
                    deviceLabel,
                    peerUsername: peer.username,
                })
            }

            if (next) {
                roomDispatch({
                    type: 'update_peer',
                    value: {
                        sessionId: sessionId,
                        peer: { remoteAudioRecordingState: next },
                    },
                })
            }

            // NOTE: there was a `remoterecording_toggle` event that only got
            // logged when start recording.
        },
        [calcCurrRemoteRecordingPeerAudio, roomStateRef, onRecordingTriggered, stopRemoteAudioRecord, roomDispatch]
    )

    const toggleRemoteMidiMute = useCallback(
        (peer: MusePeer, deviceId: string) => {
            const peerRef = roomStateRef.current.peers[peer.sessionId]
            if (!peerRef) {
                return
            }

            const next = { ...(peerRef.remoteMidiContolStates || {}) }
            const isMutedNext = !next[deviceId]?.isMuted
            next[deviceId] = {
                ...(next[deviceId] || {}),
                isMuted: isMutedNext,
            }

            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peerRef.sessionId,
                    peer: {
                        remoteMidiContolStates: next,
                    },
                },
            })

            const eventName = isMutedNext
                ? AnalyticsEvents.CLICKED_MUTE_REMOTE_MIDI_BUTTON
                : AnalyticsEvents.CLICKED_UNMUTE_REMOTE_MIDI_BUTTON
            logUserActivityWithContext(eventName, {
                deviceId,
                peerUsername: peer.username,
            })
        },
        [roomDispatch, roomStateRef]
    )

    const endCurrRemoteAudioRecord = useCallback(() => {
        const info = calcCurrRemoteRecordingPeerAudio(roomStateRef.current)
        if (info) {
            const deviceId = info.device?.info?.deviceId
            const peer = info.peer
            if (peer && deviceId) {
                toggleRemoteAudioRecord(peer, deviceId, info.device?.info.displayLabel || '')
            }
        }
    }, [calcCurrRemoteRecordingPeerAudio, roomStateRef, toggleRemoteAudioRecord])

    const calcRemoteAudioDeviceIsMutedByOwner = useCallback(
        (media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio, chgrp: number[], manifest: PeerManifest) => {
            let mutedChgrps = (manifest.localP2pAudios || []).find((a) => a.info.deviceId === media.info.deviceId)
                ?.controlStates?.mutedChannelGroups || [[]]
            if (mutedChgrps.find((g: number[]) => isSubset(chgrp, g))) {
                return true
            }

            mutedChgrps = (manifest.localSfuAudios || []).find((a) => a.info.deviceId === media.info.deviceId)
                ?.controlStates?.mutedChannelGroups || [[]]
            if (mutedChgrps.find((g: number[]) => isSubset(chgrp, g))) {
                return true
            }

            return (
                (manifest.localP2pAudios || []).find((a) => a.info.deviceId === media.info.deviceId)?.controlStates
                    ?.isMuted ||
                (manifest.localSfuAudios || []).find((a) => a.info.deviceId === media.info.deviceId)?.controlStates
                    ?.isMuted ||
                false
            )
        },
        []
    )

    const calcAllRemoteAudioDevicesAreMutedByOwner = useCallback(
        (remoteAudios: (SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio)[], manifest: PeerManifest) => {
            try {
                for (let a of remoteAudios) {
                    for (let chgrp of a.info.channelSelection) {
                        if (!calcRemoteAudioDeviceIsMutedByOwner(a, chgrp, manifest)) {
                            return false
                        }
                    }
                }
                return true
            } catch (e) {
                return false
            }
        },
        [roomStateRef]
    )

    const calcRemoteVideoDeviceIsMutedByOwner = useCallback((manifest: PeerManifest) => {
        return manifest.localP2pVideo?.controlStates?.isMuted || manifest.localSfuVideo?.controlStates?.isMuted || false
    }, [])

    const calcSessionRecordingRemotePeers = useCallback(() => {
        const sessionRecordingPeers: MusePeer[] = []
        Object.keys(roomState.peers).forEach((sessionId) => {
            const peer = roomState.peers[sessionId]
            if (peer.manifest.isScreenRecording) {
                sessionRecordingPeers.push(peer)
            }
        })
        return sessionRecordingPeers
    }, [roomState.peers])

    const pinPeerVideo = useCallback((peerId: string | null, kind: PinKind = 'camera') => {
        roomDispatch({
            type: 'pin_peer_video',
            value: {
                pinnedPeerId: peerId,
                pinKind: kind,
            },
        })
    }, [])

    const screenRecordingCommand = useCallback(
        (command: 'start' | 'stop', options?: { openSettingsOnStreamError?: boolean }) => {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    screenRecordingCommand: {
                        reqId: (roomStateRef.current.screenRecordingCommand?.reqId || 0) + 1,
                        command: command,
                        openSettingsOnStreamError: options?.openSettingsOnStreamError || false,
                    },
                },
            })
        },
        [roomDispatch, roomStateRef]
    )

    const toggleAccordionForPeer = useCallback((sessionId: string | '_me') => {
        const next = structuredClone(roomStateRef.current.accordionOpenStates)
        next[sessionId] = !roomStateRef.current.accordionOpenStates[sessionId]

        roomDispatch({
            type: 'update_room_context',
            value: {
                accordionOpenStates: next,
            },
        })
    }, [])

    const setAccordionForPeer = useCallback((sessionId: string | '_me', state: boolean | null) => {
        const next = structuredClone(roomStateRef.current.accordionOpenStates)
        if (state == null) {
            delete next[sessionId]
        } else {
            next[sessionId] = state
        }

        roomDispatch({
            type: 'update_room_context',
            value: {
                accordionOpenStates: next,
            },
        })
    }, [])

    const showAllAccordions = useCallback((state: boolean) => {
        const next = structuredClone(roomStateRef.current.accordionOpenStates)
        Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
            next[sessionId] = state
        })
        next[userStateRef.current.sessionId] = state

        roomDispatch({
            type: 'update_room_context',
            value: {
                accordionOpenStates: next,
            },
        })
    }, [])

    const calcIsAllAccordionsClosed = () => {
        let b = true // start out w/ assume closed.
        Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
            if (!roomStateRef.current.accordionOpenStates[sessionId]) {
                // if even one is open, then change.
                b = false
            }
        })
        return b
    }

    const hostControlRemoteMuteAllUsers = () => {
        broadcastMessage({
            type: 'remote-host-control-mute-client',
            data: {},
        })
    }

    const getCachedRemoteAudioVolume = (
        peer: MusePeer,
        media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio,
        chgrp: number[]
    ) => {
        const v = persistent.getAny(`peerAudioVolumeCacheByDeviceId.${peer.appSerialId}-${media.info.label}`)

        try {
            const obj = JSON.parse(v) as VolumePerChannelGroup
            for (let g of obj) {
                if (isSubset(chgrp, g.channelGroup)) {
                    return g.value
                }
            }
            return 1
        } catch (e) {
            const f = parseFloat(v)
            if (isNaN(f)) {
                return 1
            } else {
                return f
            }
        }
    }

    const checkRemotePeersAudioEngineCompatibilityAndShowDialog = useCallback(
        (tracksToCheck: AudioTrackWithInfo[], peers: { [key: string]: MusePeer }) => {
            const audioengineTrack = tracksToCheck.find((a) => a.info.isAudioEngine) || null

            let incompatiblePeers: { username: string; appVersion: string }[] = []
            Object.keys(peers).forEach((sessionId) => {
                const peer = peers[sessionId]
                if (!peer.appVersion) {
                    incompatiblePeers.push({ username: peer.username, appVersion: '' })
                    return
                }

                const appVersion = stripSemverVersion(peer.appVersion)
                if (!appVersion) {
                    incompatiblePeers.push({ username: peer.username, appVersion: peer.appVersion })
                    return
                }

                if (semver.lt(appVersion, '2.0.0')) {
                    incompatiblePeers.push({ username: peer.username, appVersion: appVersion })
                }
            })

            if (audioengineTrack && incompatiblePeers.length > 0) {
                if (!roomStateRef.current.audioEngineIncompatiblePeerWarningDialog.doNotShowAgain) {
                    roomDispatch({
                        type: 'update_room_context',
                        value: {
                            audioEngineIncompatiblePeerWarningDialog: {
                                isOpen: true,
                                doNotShowAgain: false,
                                incompatiblePeers: incompatiblePeers,
                                audioengineLabel: audioengineTrack.info.label,
                            },
                        },
                    })
                }
            }
        },
        []
    )

    const onRecentSpeakerListUpdate = useCallback((recentSpeakerList: string[]) => {
        const sfuRoom = roomStateRef.current.sfuRoom
        const myRank = recentSpeakerList.findIndex((a) => a === userStateRef.current.sessionId)
        const shouldMyAudioBeDeprioritized = myRank >= Constants.data.max_audio_subscription_users

        log.verbose('onRecentSpeakerListUpdate', 'received message.', {
            recentSpeakerList: recentSpeakerList,
            myRank,
            shouldMyAudioBeDeprioritized,
            isMyAudioDeprioritized: roomStateRef.current.isMyAudioDeprioritized,
        })

        if (roomStateRef.current.isMyAudioDeprioritized !== shouldMyAudioBeDeprioritized) {
            if (sfuRoom && shouldMyAudioBeDeprioritized) {
                let ct = 0
                sfuRoom.localParticipant.audioTrackPublications.forEach((pub) => {
                    if (pub.track) {
                        sfuRoom.localParticipant.unpublishTrack(pub.track, false)
                        ct++
                    }
                })
                log.verbose('onRecentSpeakerListUpdate', `unpublished ${ct} audio tracks.`)

                log.verbose('onRecentSpeakerListUpdate', `calling speakerDetectionManager.resetActiveState().`)
                speakerDetectionManager.resetActiveState()
            }

            if (shouldMyAudioBeDeprioritized) {
                showSnackbar({
                    severity: 'info',
                    message: 'We have muted your audio to help conserve resources.',
                })
                myAudioDeviceMuteAll()
            }

            roomDispatch({
                type: 'update_room_context',
                value: { isMyAudioDeprioritized: shouldMyAudioBeDeprioritized },
            })
        }
    }, [])

    const testConnectionFailure = useCallback(
        (types: ('signaling_ws' | 'connection')[]) => {
            if (types.includes('connection')) {
                roomStateRef.current.sfuRoom && roomStateRef.current.sfuRoom.disconnect()
                for (let sessionId of Object.keys(roomStateRef.current.peers)) {
                    const peer = roomStateRef.current.peers[sessionId]
                    try {
                        peer.p2pConnection && peer.p2pConnection.destroy()
                    } catch (e) {}
                }
            } else if (types.includes('signaling_ws')) {
                const socket = wsConnection.getSocket()
                if (socket) {
                    try {
                        socket.close()
                    } catch (e) {}
                }
            }
        },
        [roomStateRef]
    )

    return {
        loadRoom,
        joinRoom,
        leaveRoom,
        softDisconnectRoom,
        destroyRoom,
        switchRoom,
        updateRoomData,
        upgradeToSfuRoom,
        boostRoom,
        startCloudRecording,
        handleWaitlistUser,
        startRemovePeer,
        removePeer,
        leaveRoomStart,
        sendRoomUserControlMessageToServer,
        createSubroom,
        boostRoomToPro,

        // control my media device states + utility functions
        toggleMyVideoDeviceMute,
        toggleMyAudioDeviceMute,
        myAudioDeviceMuteAll,
        toggleMyAudioDeviceTalkback,
        toggleAutoMute,
        toggleMyMidiDeviceMute,
        isMyVideoDeviceMuted,
        calcIsMyAudioDeviceAllMuted,
        openRecordTalkbackKeyDialog,
        closeRecordTalkbackKeyDialog,
        talkbackKeyStateChangeHandler,

        // control remote device states + utility functions
        toggleMuteAllPeers,
        toggleRemoteVideoMute,
        toggleRemoteAudioMute,
        toggleRemoteAudioSolo,
        changeRemoteAudioVolume,
        stopRemoteAudioRecord,
        toggleRemoteAudioRecord,
        toggleRemoteMidiMute,
        endCurrRemoteAudioRecord,
        calcIsRemoteAudioDeviceAllMuted,
        isRemoteAudioDeviceAllMutedForAllPeers,
        calcIsRemoteAudioDeviceAllSoloed,
        calcRemoteAudioDeviceIsMutedByOwner,
        calcRemoteVideoDeviceIsMutedByOwner,
        calcAllRemoteAudioDevicesAreMutedByOwner,
        calcSessionRecordingRemotePeers,
        pinPeerVideo,

        // remote recording
        onStartRemoteRecordingHandler,
        onStopRemoteRecordingHandler,
        onRemoteRecordingErrorHandler,
        onRemoteRecordingChunkHandler,
        calcCurrRemoteRecordingPeerAudio,
        onRecordingTriggered,

        // screen recording commands
        screenRecordingCommand,

        // room sidebar peer accordion controls
        toggleAccordionForPeer,
        setAccordionForPeer,
        showAllAccordions,
        calcIsAllAccordionsClosed,

        // host room control
        hostControlRemoteMuteAllUsers,

        // utils + etc
        sendMessageToPeer,
        broadcastMessage,

        getCachedRemoteAudioVolume,

        foreachRemoteAudioDevicesOfPeer,
        foreachRemoteAudioDevices,

        checkRemotePeersAudioEngineCompatibilityAndShowDialog,
        onRecentSpeakerListUpdate,

        testConnectionFailure,
    }
}
